import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface PendingAcknowledgement {
  policy_id: string;
  policy_name: string;
  policy_category: string;
  version_id: string;
  version_number: number;
  file_url: string;
  approved_by: string | null;
}

export function usePendingAcknowledgements() {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ['pending-acknowledgements', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return [];

      // Get all policies in user's clinic that require acknowledgement
      // Only show acknowledgements for approved/locked policies, never pending_approval or draft
      const { data: policies, error: policiesError } = await supabase
        .from('policies')
        .select('id, name, category, requires_acknowledgement, status')
        .eq('requires_acknowledgement', true)
        .eq('status', 'Approved')
        .or('is_archived.is.null,is_archived.eq.false');

      if (policiesError) throw policiesError;
      if (!policies?.length) return [];

      // Get the latest version for each policy
      const policyIds = policies.map(p => p.id);
      const { data: versions, error: versionsError } = await supabase
        .from('policy_versions')
        .select('id, policy_id, version_number, file_url, approved_by')
        .in('policy_id', policyIds)
        .order('version_number', { ascending: false });

      if (versionsError) throw versionsError;
      if (!versions?.length) return [];

      // Get latest version per policy
      const latestVersions = new Map<string, typeof versions[0]>();
      versions.forEach(v => {
        if (!latestVersions.has(v.policy_id)) {
          latestVersions.set(v.policy_id, v);
        }
      });

      // Get user's existing acknowledgements
      const versionIds = Array.from(latestVersions.values()).map(v => v.id);
      const { data: acks, error: acksError } = await supabase
        .from('policy_acknowledgements')
        .select('policy_version_id')
        .eq('acknowledged_by', profile.id)
        .in('policy_version_id', versionIds);

      if (acksError) throw acksError;

      const acknowledgedVersionIds = new Set(acks?.map(a => a.policy_version_id) ?? []);

      // Filter to pending acknowledgements
      const pending: PendingAcknowledgement[] = [];
      
      latestVersions.forEach((version, policyId) => {
        // Skip if already acknowledged
        if (acknowledgedVersionIds.has(version.id)) return;

        const policy = policies.find(p => p.id === policyId);
        if (policy) {
          pending.push({
            policy_id: policyId,
            policy_name: policy.name,
            policy_category: policy.category,
            version_id: version.id,
            version_number: version.version_number,
            file_url: version.file_url,
            approved_by: version.approved_by,
          });
        }
      });

      return pending;
    },
    enabled: !!profile?.id,
    staleTime: 30000, // 30 seconds
  });
}
